import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
    CITIES,
    applyCohortFilters,
    createCohort,
    deleteCohort,
    fetchCohortMembers,
    fetchCohorts,
    patchCohort,
    previewCohort,
} from "@/lib/api";
import { Field, PageHeader } from "../_shared";

const TEMPLATE_KEYS = [
    { key: "application_submitted", label: "Application Submitted" },
    { key: "under_review", label: "Under Review" },
    { key: "interview_scheduled", label: "Interview Scheduled" },
    { key: "selected", label: "Selected" },
    { key: "not_selected", label: "Not Selected" },
];

const COHORT_STATUSES = [
    "received",
    "shortlisted",
    "interview_scheduled",
    "selected",
    "rejected",
    "closed",
];

export default function CohortsTab() {
    const [cohorts, setCohorts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            setCohorts(await fetchCohorts());
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    return (
        <div data-testid="cohorts-tab">
            <PageHeader
                eyebrow="Audience"
                title="Cohorts"
                accent="& slicers."
                description="Bundle applicants by city / status / search and override emails per template."
                actions={
                    <button
                        type="button"
                        className="vc-btn"
                        onClick={() =>
                            setEditing({
                                _new: true,
                                name: "",
                                slug: "",
                                description: "",
                                filters: {
                                    cities: [],
                                    statuses: [],
                                    q: "",
                                    date_from: "",
                                    date_to: "",
                                },
                                template_overrides: {},
                            })
                        }
                        data-testid="cohort-create-btn"
                    >
                        + New cohort
                    </button>
                }
            />

            {loading && <div className="text-ink-soft">Loading cohorts…</div>}
            {!loading && cohorts.length === 0 && (
                <div className="border border-line rounded-2xl p-8 text-center text-ink-soft">
                    No cohorts yet. Create one to slice applications by city +
                    status with custom emails.
                </div>
            )}

            <div
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
                data-testid="cohorts-grid"
            >
                {cohorts.map((c) => (
                    <CohortCard
                        key={c.id}
                        cohort={c}
                        onEdit={() => setEditing(c)}
                        onChanged={refresh}
                    />
                ))}
            </div>

            {editing && (
                <CohortEditor
                    initial={editing}
                    onClose={() => setEditing(null)}
                    onSaved={async () => {
                        setEditing(null);
                        await refresh();
                    }}
                />
            )}
        </div>
    );
}

function CohortCard({ cohort, onEdit, onChanged }) {
    const [busy, setBusy] = useState(false);
    const [members, setMembers] = useState(null);
    const [open, setOpen] = useState(false);

    const f = cohort.filters || {};
    const filterChips = [
        ...(f.cities || []).map((c) => ({ k: "city", v: c })),
        ...(f.statuses || []).map((s) => ({ k: "status", v: s })),
        ...(f.q ? [{ k: "q", v: f.q }] : []),
    ];
    const overrideCount = Object.keys(cohort.template_overrides || {}).filter(
        (k) =>
            cohort.template_overrides[k]?.subject ||
            cohort.template_overrides[k]?.html,
    ).length;

    const onApply = async () => {
        setBusy(true);
        try {
            await applyCohortFilters(cohort.id);
            toast("Filters applied - cohort members refreshed.");
            await onChanged();
        } catch (e) {
            toast(e?.response?.data?.detail || "Failed to apply filters.");
        } finally {
            setBusy(false);
        }
    };

    const onPreview = async () => {
        setBusy(true);
        try {
            const r = await previewCohort(cohort.id);
            toast(`${r.total} application(s) match these filters.`);
        } finally {
            setBusy(false);
        }
    };

    const onDelete = async () => {
        if (!window.confirm(`Delete cohort "${cohort.name}"?`)) return;
        setBusy(true);
        try {
            await deleteCohort(cohort.id);
            toast("Cohort deleted.");
            await onChanged();
        } finally {
            setBusy(false);
        }
    };

    const onToggleMembers = async () => {
        if (open) {
            setOpen(false);
            return;
        }
        setBusy(true);
        try {
            setMembers(await fetchCohortMembers(cohort.id));
            setOpen(true);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            data-testid={`cohort-card-${cohort.slug}`}
            className="border border-line rounded-2xl bg-background overflow-hidden flex flex-col"
        >
            <div className="p-5 border-b border-line">
                <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                        <div className="text-[0.6rem] tracking-[0.25em] uppercase text-ink-soft mb-1">
                            {cohort.slug}
                        </div>
                        <h3 className="font-display font-bold text-xl leading-tight truncate">
                            {cohort.name}
                        </h3>
                    </div>
                    <div
                        className="font-display font-black text-2xl text-gradient leading-none whitespace-nowrap"
                        title="Members"
                    >
                        {cohort.member_count}
                    </div>
                </div>
                {cohort.description && (
                    <p className="text-sm text-ink-soft mt-3 line-clamp-2">
                        {cohort.description}
                    </p>
                )}
                <div className="flex flex-wrap gap-1.5 mt-4">
                    {filterChips.length === 0 && (
                        <span className="text-[10px] tracking-[0.2em] uppercase text-ink-soft">
                            No filters
                        </span>
                    )}
                    {filterChips.map((c, i) => (
                        <span
                            key={i}
                            className="text-[10px] tracking-[0.16em] uppercase px-2 py-1 rounded-full border border-line text-ink-soft"
                        >
                            {c.k}: {c.v}
                        </span>
                    ))}
                </div>
                {overrideCount > 0 && (
                    <div className="text-[10px] tracking-[0.18em] uppercase text-[#90FDA6] mt-3">
                        {overrideCount} email template
                        {overrideCount > 1 ? "s" : ""} overridden
                    </div>
                )}
            </div>
            <div className="p-3 flex flex-wrap gap-2 text-xs">
                <button
                    type="button"
                    onClick={onApply}
                    disabled={busy}
                    className="vc-btn vc-btn-ghost"
                    data-testid={`cohort-apply-${cohort.slug}`}
                >
                    {busy ? "…" : "Apply filters"}
                </button>
                <button
                    type="button"
                    onClick={onPreview}
                    disabled={busy}
                    className="vc-btn vc-btn-ghost"
                >
                    Preview
                </button>
                <button
                    type="button"
                    onClick={onToggleMembers}
                    disabled={busy}
                    className="vc-btn vc-btn-ghost"
                >
                    {open ? "Hide" : "View"}
                </button>
                <button
                    type="button"
                    onClick={onEdit}
                    disabled={busy}
                    className="vc-btn vc-btn-ghost"
                    data-testid={`cohort-edit-${cohort.slug}`}
                >
                    Edit
                </button>
                <button
                    type="button"
                    onClick={onDelete}
                    disabled={busy}
                    className="vc-btn vc-btn-ghost text-[#ff6b6b]"
                >
                    Delete
                </button>
            </div>
            {open && members && (
                <div className="border-t border-line p-3 text-xs max-h-72 overflow-y-auto space-y-1">
                    {members.length === 0 && (
                        <div className="text-ink-soft">No members yet.</div>
                    )}
                    {members.map((m) => (
                        <div
                            key={m.reference_id}
                            className="flex items-center justify-between py-1"
                        >
                            <span className="font-mono text-ink-soft">
                                {m.reference_id}
                            </span>
                            <span className="truncate ml-3 flex-1">
                                {m.full_name} · {m.city}
                            </span>
                            <span className="text-[10px] tracking-[0.16em] uppercase text-ink-soft ml-2">
                                {m.status}
                            </span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function CohortEditor({ initial, onClose, onSaved }) {
    const isNew = !!initial._new;
    const [name, setName] = useState(initial.name || "");
    const [slug, setSlug] = useState(initial.slug || "");
    const [description, setDescription] = useState(initial.description || "");
    const [cities, setCities] = useState(initial.filters?.cities || []);
    const [statuses, setStatuses] = useState(initial.filters?.statuses || []);
    const [q, setQ] = useState(initial.filters?.q || "");
    const [overrides, setOverrides] = useState(initial.template_overrides || {});
    const [busy, setBusy] = useState(false);

    const toggleCity = (c) =>
        setCities((prev) =>
            prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c],
        );
    const toggleStatus = (s) =>
        setStatuses((prev) =>
            prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s],
        );

    const setOverride = (key, field, val) =>
        setOverrides((prev) => ({
            ...prev,
            [key]: { ...(prev[key] || {}), [field]: val },
        }));

    const onSave = async () => {
        const cleanedOverrides = Object.fromEntries(
            Object.entries(overrides).filter(
                ([, v]) => v?.subject || v?.html,
            ),
        );
        const payload = {
            name: name.trim(),
            description: description.trim(),
            filters: {
                cities,
                statuses,
                q: q.trim() || null,
                date_from: null,
                date_to: null,
            },
            template_overrides: cleanedOverrides,
        };
        if (!name.trim()) {
            toast("Name is required.");
            return;
        }
        setBusy(true);
        try {
            if (isNew) {
                if (!slug.trim()) {
                    toast("Slug is required.");
                    setBusy(false);
                    return;
                }
                await createCohort({ ...payload, slug: slug.trim() });
                toast("Cohort created.");
            } else {
                await patchCohort(initial.id, payload);
                toast("Cohort updated.");
            }
            await onSaved();
        } catch (e) {
            toast(e?.response?.data?.detail || "Save failed.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-8 px-4"
            onClick={onClose}
            data-testid="cohort-editor-modal"
        >
            <div
                className="relative w-full max-w-3xl bg-background border border-line rounded-2xl p-6 md:p-8 space-y-6"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-start justify-between gap-3">
                    <h2
                        className="font-display font-black uppercase tracking-tightest"
                        style={{ fontSize: "clamp(1.4rem, 2.6vw, 2rem)" }}
                    >
                        {isNew ? "New cohort" : initial.name}
                    </h2>
                    <button
                        type="button"
                        onClick={onClose}
                        className="text-ink-soft hover:text-ink text-sm uppercase tracking-[0.18em]"
                    >
                        Close
                    </button>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                    <Field label="Name">
                        <input
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="w-full bg-transparent border border-line rounded-lg px-3 py-2 outline-none focus:border-white/40"
                            data-testid="cohort-name"
                        />
                    </Field>
                    <Field label={isNew ? "Slug (locked once set)" : "Slug"}>
                        <input
                            value={slug}
                            onChange={(e) =>
                                setSlug(
                                    e.target.value
                                        .toLowerCase()
                                        .replace(/[^a-z0-9-]/g, "-"),
                                )
                            }
                            disabled={!isNew}
                            className="w-full bg-transparent border border-line rounded-lg px-3 py-2 outline-none focus:border-white/40 disabled:opacity-50 font-mono text-sm"
                            data-testid="cohort-slug"
                        />
                    </Field>
                </div>

                <Field label="Description">
                    <textarea
                        rows={2}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        className="w-full bg-transparent border border-line rounded-lg px-3 py-2 outline-none focus:border-white/40 resize-none"
                    />
                </Field>

                <div>
                    <div className="vc-section-label text-ink-soft mb-3">
                        Filters
                    </div>
                    <div className="space-y-4">
                        <div>
                            <div className="text-[10px] tracking-[0.25em] uppercase text-ink-soft mb-2">
                                Cities
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {CITIES.map((c) => (
                                    <button
                                        key={c}
                                        type="button"
                                        onClick={() => toggleCity(c)}
                                        className={`px-3 py-1.5 rounded-full text-xs uppercase tracking-[0.14em] border transition-colors ${cities.includes(c) ? "bg-ink text-background border-ink" : "border-line text-ink-soft hover:text-ink"}`}
                                    >
                                        {c}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div>
                            <div className="text-[10px] tracking-[0.25em] uppercase text-ink-soft mb-2">
                                Statuses
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {COHORT_STATUSES.map((s) => (
                                    <button
                                        key={s}
                                        type="button"
                                        onClick={() => toggleStatus(s)}
                                        className={`px-3 py-1.5 rounded-full text-xs uppercase tracking-[0.14em] border transition-colors ${statuses.includes(s) ? "bg-ink text-background border-ink" : "border-line text-ink-soft hover:text-ink"}`}
                                    >
                                        {s}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <Field label="Search (name, email, ref, why_you, who_youd_bring)">
                            <input
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                placeholder="optional free-text filter"
                                className="w-full bg-transparent border border-line rounded-lg px-3 py-2 outline-none focus:border-white/40"
                            />
                        </Field>
                    </div>
                </div>

                <div>
                    <div className="vc-section-label text-ink-soft mb-3">
                        Email template overrides
                    </div>
                    <div className="space-y-3">
                        {TEMPLATE_KEYS.map((t) => (
                            <OverrideRow
                                key={t.key}
                                tkey={t.key}
                                label={t.label}
                                value={overrides[t.key] || {}}
                                onChange={(field, val) =>
                                    setOverride(t.key, field, val)
                                }
                            />
                        ))}
                    </div>
                </div>

                <div className="flex items-center gap-3 pt-2">
                    <button
                        type="button"
                        onClick={onSave}
                        disabled={busy}
                        className="vc-btn"
                        data-testid="cohort-save"
                    >
                        {busy ? "Saving…" : isNew ? "Create" : "Save changes"}
                    </button>
                    <button
                        type="button"
                        onClick={onClose}
                        className="vc-btn vc-btn-ghost"
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
}

function OverrideRow({ tkey, label, value, onChange }) {
    const [open, setOpen] = useState(!!(value.subject || value.html));
    return (
        <div className="border border-line rounded-lg">
            <button
                type="button"
                onClick={() => setOpen(!open)}
                className="w-full flex items-center justify-between px-4 py-3 text-left"
            >
                <span className="text-xs uppercase tracking-[0.18em]">
                    {label}
                </span>
                <span className="text-[10px] tracking-[0.2em] uppercase text-ink-soft">
                    {value.subject || value.html ? "Custom" : "Default"} ·{" "}
                    {open ? "Hide" : "Show"}
                </span>
            </button>
            {open && (
                <div className="px-4 pb-4 space-y-3">
                    <Field label="Subject override (leave blank to use default)">
                        <input
                            value={value.subject || ""}
                            onChange={(e) => onChange("subject", e.target.value)}
                            data-testid={`override-subject-${tkey}`}
                            className="w-full bg-transparent border border-line rounded-lg px-3 py-2 outline-none focus:border-white/40 text-sm"
                        />
                    </Field>
                    <Field label="HTML body override (leave blank to use default)">
                        <textarea
                            rows={6}
                            value={value.html || ""}
                            onChange={(e) => onChange("html", e.target.value)}
                            data-testid={`override-html-${tkey}`}
                            placeholder="<!doctype html>…"
                            className="w-full bg-transparent border border-line rounded-lg px-3 py-2 outline-none focus:border-white/40 text-xs font-mono resize-none"
                        />
                    </Field>
                </div>
            )}
        </div>
    );
}
